import { Link, type MetaFunction } from "react-router";

import { LegalDocument } from "@/legal/document";

export const meta: MetaFunction = () => [
  { title: `Terms of Service · ${publicEnv.siteTitle}` },
];

// Set the agreement for paid private capture, deliberate publication, public X
// embeds, and Bookmarked's recurring subscription lifecycle.
//
export default function Terms() {
  return (
    <LegalDocument
      description="The agreement for saving public X post links and publishing deliberate collections with Bookmarked FYI."
      title="Terms of Service"
      updated="August 11, 2026"
    >
      <section>
        <h2>1. Agreement and operator</h2>
        <p>
          These Terms govern your access to Bookmarked FYI (the
          &ldquo;Service&rdquo;), operated by {publicEnv.siteLegalEntity}
          (&ldquo;Bookmarked,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
          &ldquo;our&rdquo;). By accessing the Service or creating an account,
          you agree to these Terms and our{" "}
          <Link to="/privacy">Privacy Policy</Link>. If you do not agree, do not
          use the Service.
        </p>
      </section>

      <section>
        <h2>2. Eligibility and accounts</h2>
        <p>
          You must be at least 13 years old and legally permitted to use the
          Service. If you have not reached the age of legal majority where you
          live, a parent or guardian must review and agree to these Terms for
          you. If you use the Service for an organization, you represent that
          you can bind it.
        </p>
        <p>
          Sign-in with X verifies control of an X account and its current
          handle. You are responsible for activity through your Bookmarked
          account and for protecting the devices and X account used to sign in.
          Contact us promptly if you suspect unauthorized access.
        </p>
      </section>

      <section>
        <h2>3. The Service and publication choices</h2>
        <p>
          Bookmarked lets you paste links to individual public X posts, keep a
          private Inbox, organize saved links and your own notes into
          collections, and intentionally publish selected collections. We do not
          import private X Bookmarks or automatically publish an Inbox.
        </p>
        <p>
          A collection is private until you use the publication control. Once
          public, anyone can view, index, share, or copy the public page and the
          curator context shown there. Unpublishing prevents future access
          through Bookmarked but cannot recall copies already made by others.
        </p>
      </section>

      <section>
        <h2>4. Trials, subscriptions, and billing</h2>
        <p>
          Access to the curator Studio requires a recurring subscription. The
          available price, billing interval, included features, and any trial
          period are shown on <Link to="/pricing">Pricing</Link>. Starting from
          a pricing card uses the selected plan; entering through Studio without
          choosing a card uses the configured default available plan. Review
          Pricing before completing sign-in. Completing sign-in asks us to
          create that subscription for your Bookmarked account.
        </p>
        <p>
          Stripe processes payment methods and charges. Unless you cancel, a
          subscription renews automatically at the disclosed interval and Stripe
          may charge the payment method on file for the price and applicable
          taxes. If a trial is offered, the first charge occurs when it ends
          unless you cancel before then. We will give notice required by law
          before a material price change applies to a later renewal.
        </p>
        <p>
          You can change an available plan, update a payment method, or manage
          cancellation from <Link to="/billing">Billing</Link>. A cancellation
          normally takes effect at the end of the current paid period, and
          access continues until then. Except where required by law or expressly
          stated at purchase, charges already paid are non-refundable and
          partial periods are not refunded.
        </p>
        <p>
          If a trial or paid period ends, a payment fails, or a subscription is
          paused or canceled, Studio editing may stop. Your saved library
          remains stored and collections already published remain public while
          you resolve billing, subject to these Terms and any later retention
          notice we provide.
        </p>
      </section>

      <section>
        <h2>5. Your content and permissions</h2>
        <p>
          You retain any rights you have in profile text, fallback titles,
          notes, collection descriptions, arrangements, and other material you
          submit (&ldquo;Your Content&rdquo;). You grant us a worldwide,
          non-exclusive, royalty-free license to host, copy, process, and
          display Your Content only as needed to operate, secure, and improve
          the Service and to show collections you choose to publish.
        </p>
        <p>
          You are responsible for Your Content and must have the rights needed
          to submit and publish it. Do not include confidential information in a
          public collection. A link, X embed, and attribution do not grant you
          ownership of or a license to the underlying post or media.
        </p>
      </section>

      <section>
        <h2>6. X, Stripe, and third-party content</h2>
        <p>
          X provides identity authentication and the widget used to render
          public posts. X content remains hosted and controlled by X and its
          users, subject to their own terms and policies. A post may stop
          displaying if it is deleted, made private, blocked, or otherwise
          unavailable. Bookmarked is not affiliated with or endorsed by X.
        </p>
        <p>
          Stripe provides payment and subscription services under its own terms
          and policies. Other links may lead to services we do not control. We
          are not responsible for third-party content, availability, security,
          or data practices.
        </p>
      </section>

      <section>
        <h2>7. Acceptable use</h2>
        <p>You may not use the Service to:</p>
        <ul>
          <li>break the law or violate another person&apos;s rights;</li>
          <li>publish deceptive, infringing, abusive, or unlawful material;</li>
          <li>impersonate someone or manipulate a verified handle claim;</li>
          <li>
            expose private information without authorization or use Bookmarked
            to evade an X restriction;
          </li>
          <li>
            probe, disrupt, overload, scrape, or bypass security, rate limits,
            or access controls; or
          </li>
          <li>
            introduce malware or interfere with another person&apos;s use.
          </li>
        </ul>
        <p>
          We may remove content or restrict access when reasonably necessary to
          enforce these Terms, protect people or the Service, or comply with
          law.
        </p>
      </section>

      <section>
        <h2>8. Our intellectual property</h2>
        <p>
          The Service, software, design, branding, and original materials belong
          to us or our licensors. Subject to these Terms, we grant you a
          limited, revocable, non-transferable right to use the Service for its
          intended purpose. No other rights are granted. If you provide
          feedback, we may use it without restriction or payment to you.
        </p>
      </section>

      <section>
        <h2>9. Availability and changes</h2>
        <p>
          We may improve, limit, replace, or discontinue the Service or an
          integration. The Service may be unavailable because of maintenance,
          failures, provider changes, or events beyond our control. Keep your
          own copy of information you cannot afford to lose; Bookmarked is not
          an archival copy of the underlying X posts.
        </p>
      </section>

      <section>
        <h2>10. Suspension, termination, and deletion</h2>
        <p>
          You may stop using the Service, cancel a subscription, or request
          account deletion at any time. Canceling does not itself delete your
          account. We may suspend or terminate access for nonpayment, a material
          or repeated breach, legal risk, abuse, or a threat to the Service or
          others. When reasonable, we will provide notice and an opportunity to
          correct the issue.
        </p>
        <p>
          Provisions that by their nature should continue after termination
          survive, including ownership, disclaimers, liability limits, and
          dispute terms.
        </p>
      </section>

      <section>
        <h2>11. Disclaimers</h2>
        <p>
          TO THE EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED &ldquo;AS
          IS&rdquo; AND &ldquo;AS AVAILABLE.&rdquo; WE DISCLAIM IMPLIED
          WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT GUARANTEE
          UNINTERRUPTED OPERATION, PRESERVATION OF CONTENT, A PARTICULAR
          AUDIENCE, OR CONTINUED ACCESS TO X CONTENT OR SERVICES.
        </p>
        <p>
          Nothing in these Terms excludes a warranty or consumer right that
          cannot lawfully be excluded.
        </p>
      </section>

      <section>
        <h2>12. Limitation of liability</h2>
        <p>
          TO THE EXTENT PERMITTED BY LAW, NEITHER WE NOR OUR SUPPLIERS WILL BE
          LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR
          PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, GOODWILL, DATA, OR
          BUSINESS INTERRUPTION. OUR TOTAL LIABILITY ARISING OUT OF THE SERVICE
          OR THESE TERMS WILL NOT EXCEED THE GREATER OF $100 OR THE AMOUNT YOU
          PAID US FOR THE SERVICE DURING THE 12 MONTHS BEFORE THE EVENT GIVING
          RISE TO THE CLAIM. THESE LIMITS APPLY TO THE FULLEST EXTENT PERMITTED
          BY LAW.
        </p>
      </section>

      <section>
        <h2>13. Indemnification</h2>
        <p>
          To the extent permitted by law, you will defend and indemnify us from
          third-party claims arising from Your Content, your misuse of the
          Service, or your violation of these Terms or another person&apos;s
          rights. This does not require indemnification for our own unlawful
          conduct.
        </p>
      </section>

      <section>
        <h2>14. Governing law and disputes</h2>
        <p>
          Washington law governs these Terms, without regard to conflict-of-law
          rules. Before filing a claim, contact us and allow 30 days for an
          informal resolution. If that does not resolve the dispute, the state
          and federal courts with jurisdiction in King County, Washington will
          have exclusive jurisdiction, except that an eligible individual claim
          may be brought in small claims court.
        </p>
        <p>
          Mandatory consumer protections in your home jurisdiction continue to
          apply where they cannot be waived.
        </p>
      </section>

      <section>
        <h2>15. Changes and general terms</h2>
        <p>
          We may update these Terms. We will post the updated date and provide
          reasonable notice before a material change takes effect. Continued use
          after the effective date means you accept the updated Terms.
        </p>
        <p>
          These Terms and the policies they incorporate are the entire agreement
          about the Service. If one provision is unenforceable, the remaining
          provisions continue. A failure to enforce a provision is not a waiver.
          You may not transfer this agreement without our consent; we may
          transfer it as part of a reorganization, financing, merger, or sale of
          the Service.
        </p>
      </section>

      <section>
        <h2>16. Contact</h2>
        <p>
          Questions about these Terms may be sent to{" "}
          <a href={`mailto:${publicEnv.siteSupportEmail}`}>
            {publicEnv.siteSupportEmail}
          </a>
          .
        </p>
      </section>
    </LegalDocument>
  );
}
